function wait(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

function debounce(fn, ms) {
    let timer = null;

    return (...args) => {
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(() => fn(...args), ms);
    };
}

/**
 * o-filters: progressive enhancement for the reusable archive filter bar.
 *
 * Without JS the bar is a GET form. With JS, toggling a checkbox filters the
 * listing via admin-ajax, swaps the results + pagination, updates the count
 * badge, re-inits the Foundation off-canvas panels on the new nodes, and syncs
 * the URL with the History API. Adapted from project-filter.js.
 */
export default class Filters {
    constructor() {
        const root = document.querySelector('.js-filters');

        if (!root) return;

        this.root = root;
        this.form = root.querySelector('.js-filters__form');
        this.inputs = [...root.querySelectorAll('.js-filters__input')];
        this.countEl = root.querySelector('[data-filters-count]');
        this.countSrEl = root.querySelector('[data-filters-count-sr]');

        this.postType = root.dataset.filtersPostType || '';
        this.list = root.dataset.filtersTarget ? document.querySelector(root.dataset.filtersTarget) : null;
        this.pagination = root.dataset.filtersPagination
            ? document.querySelector(root.dataset.filtersPagination)
            : null;

        this.cfg = window.quadrantFilters;

        if (!this.cfg || !this.cfg.ajaxUrl || !this.form || !this.list) return;

        const pagedMatch = window.location.pathname.match(/\/page\/(\d+)/);
        this.paged = pagedMatch ? parseInt(pagedMatch[1], 10) : 1;
        this.controller = null;

        this.initTypeSwitch();

        // Filtering is instant under JS; the no-JS submit must not navigate.
        this.form.addEventListener('submit', (e) => e.preventDefault());

        const onChange = debounce(() => this.load(1), 250);
        this.inputs.forEach((input) => input.addEventListener('change', onChange));

        this.pagination?.addEventListener('click', (e) => this.onPaginationClick(e));
        window.addEventListener('popstate', () => this.onPopState());
    }

    // Optional News/Insights-style switch: an external tab list feeds this same
    // engine, so the active type rides along with every request as a persistent
    // constraint. Inert on archives that don't declare data-filters-type-param.
    initTypeSwitch() {
        this.typeParam = this.root.dataset.filtersTypeParam || '';
        this.defaultType = this.root.dataset.filtersDefaultType || '';
        this.tabs = [];
        this.activeType = '';

        if (!this.typeParam) return;

        const tabsEl = this.root.dataset.filtersTabs
            ? document.querySelector(this.root.dataset.filtersTabs)
            : null;
        this.tabs = tabsEl ? [...tabsEl.querySelectorAll('[data-filter-type]')] : [];

        const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-current') === 'page');
        this.activeType = activeTab?.dataset.filterType || this.defaultType;

        this.tabs.forEach((tab) => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();

                const { filterType: value } = tab.dataset;
                if (!value || value === this.activeType) return;

                this.setActiveType(value);
                this.load(1);
            });
        });
    }

    setActiveType(value) {
        this.activeType = value;

        this.tabs.forEach((tab) => {
            if (tab.dataset.filterType === value) {
                tab.setAttribute('aria-current', 'page');
            } else {
                tab.removeAttribute('aria-current');
            }
        });
    }

    selectedParams() {
        const params = new URLSearchParams();

        this.inputs
            .filter((input) => input.checked)
            .forEach((input) => params.append(input.name, input.value));

        return params;
    }

    // Request always carries the active type (server resolves the default anyway);
    // the URL omits it when default so the canonical view stays a clean URL.
    requestParams() {
        const params = this.selectedParams();

        if (this.typeParam && this.activeType) {
            params.set(this.typeParam, this.activeType);
        }

        return params;
    }

    load(paged, { push = true, onSuccess } = {}) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const { controller } = this;

        const fadeMs = parseFloat(getComputedStyle(this.list).transitionDuration) * 1000 || 0;

        this.list.setAttribute('aria-busy', 'true');
        this.list.classList.add('is-loading');

        const body = this.requestParams();
        body.set('action', 'archive_filter');
        body.set('nonce', this.cfg.nonce);
        body.set('post_type', this.postType);
        body.set('paged', paged);

        const request = fetch(this.cfg.ajaxUrl, {
            method: 'POST',
            body,
            signal: controller.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        }).then((r) => r.json());

        return Promise.all([request, wait(fadeMs)])
            .then(([data]) => {
                if (controller !== this.controller) return;
                if (!data || !data.success) return;

                this.list.innerHTML = data.data.items;
                if (this.pagination) this.pagination.innerHTML = data.data.pagination;

                this.paged = paged;
                this.updateCount(data.data.count);
                this.reinitOffCanvas();

                if (push) this.pushUrl(paged);
                if (onSuccess) onSuccess();
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                console.error(err);
            })
            .finally(() => {
                if (controller !== this.controller) return;
                this.list.classList.remove('is-loading');
                this.list.setAttribute('aria-busy', 'false');
            });
    }

    updateCount(count) {
        const n = parseInt(count, 10) || 0;

        if (this.countEl) {
            this.countEl.textContent = n;
            this.countEl.toggleAttribute('hidden', n === 0);
        }

        if (this.countSrEl) {
            this.countSrEl.textContent = `${n} ${n === 1 ? 'filter' : 'filters'} active`;
        }
    }

    // Team/Partners render an off-canvas panel per item; the swapped nodes need
    // re-binding or the profile panels won't open. Foundation is bound to jQuery.
    reinitOffCanvas() {
        if (window.jQuery) window.jQuery(this.list).foundation();
    }

    onPaginationClick(e) {
        const link = e.target.closest('a.page-numbers');

        if (!link || link.classList.contains('current') || link.classList.contains('dots')) return;

        e.preventDefault();

        const href = link.getAttribute('href') || '';
        const match = href.match(/\/page\/(\d+)/) || href.match(/[?&]paged=(\d+)/);
        const page = match ? parseInt(match[1], 10) : 1;

        this.load(page, { onSuccess: () => this.focusList() });
    }

    focusList() {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.list.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        this.list.focus({ preventScroll: true });
    }

    pushUrl(paged) {
        const base = window.location.pathname.replace(/\/page\/\d+\/?$/, '/');
        const path = paged > 1 ? `${base}page/${paged}/` : base;
        const params = this.selectedParams();

        if (this.typeParam && this.activeType && this.activeType !== this.defaultType) {
            params.set(this.typeParam, this.activeType);
        }

        const query = params.toString();

        window.history.pushState({ paged }, '', query ? `${path}?${query}` : path);
    }

    onPopState() {
        const params = new URLSearchParams(window.location.search);

        this.inputs.forEach((input) => {
            input.checked = params.getAll(input.name).includes(input.value);
        });

        if (this.typeParam) {
            const raw = params.get(this.typeParam);
            const value = this.tabs.some((tab) => tab.dataset.filterType === raw) ? raw : this.defaultType;
            this.setActiveType(value);
        }

        const match = window.location.pathname.match(/\/page\/(\d+)/);
        const paged = match ? parseInt(match[1], 10) : 1;

        this.load(paged, { push: false });
    }
}
